/**
 * Действия со списком контактов (изменение v1.6).
 *
 * Сервер — источник истины: `PUT /contacts/{userId}` и `DELETE /contacts/{userId}` меняют
 * список, а реплика обновляется сразу из ответа, чтобы экран не ждал sync-события
 * (`contact.created` / `contact.deleted` придёт следом и на другие устройства).
 */
import { useCallback, useState } from 'react'

import { api, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import { contactsRepo, useReplica } from '@shared/db'
import { useToast } from '@shared/ui'

/** Есть ли человек в моих контактах — по реплике, без запроса. */
export function useIsContact(userId: string | null): boolean {
  const value = useReplica(async () => (userId ? contactsRepo.has(userId) : false), [userId], false)
  return value ?? false
}

export function useContactActions(): {
  busy: boolean
  add: (userId: string) => Promise<boolean>
  remove: (userId: string) => Promise<boolean>
} {
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  const add = useCallback(
    async (userId: string): Promise<boolean> => {
      setBusy(true)
      try {
        const contact = unwrap(await api.PUT('/contacts/{userId}', { params: { path: { userId } } }))
        await contactsRepo.put(contact)
        toast.show('Добавлен в контакты')
        return true
      } catch (error) {
        toast.show(
          error instanceof ApiError && error.code === 'user_unavailable'
            ? 'Пользователь недоступен'
            : 'Не удалось добавить в контакты',
          { tone: 'danger' },
        )
        return false
      } finally {
        setBusy(false)
      }
    },
    [toast],
  )

  const remove = useCallback(
    async (userId: string): Promise<boolean> => {
      setBusy(true)
      try {
        // 204 без тела: `unwrap` здесь неприменим — ошибку бросает middleware клиента
        await api.DELETE('/contacts/{userId}', { params: { path: { userId } } })
        await contactsRepo.remove(userId)
        toast.show('Удалён из контактов')
        return true
      } catch {
        toast.show('Не удалось удалить из контактов', { tone: 'danger' })
        return false
      } finally {
        setBusy(false)
      }
    },
    [toast],
  )

  return { busy, add, remove }
}
